/**
 * Strategy-doc panel for the business page.
 *
 * Two stacked sections:
 *   1. Call transcripts — scanned from a per-(org, business) GCS folder.
 *   2. Strategy docs — past generation runs with status + a button to
 *      generate a new one. Clicking a succeeded run opens a drawer with
 *      the rendered markdown.
 *
 * The backend is the system of record. This panel only displays state +
 * triggers `POST /api/strategy/doc/runs`; it never invents data.
 */

import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Download,
  FileText,
  FileUp,
  Pencil,
  Save,
  AlertCircle,
  CheckCircle2,
  Upload,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { PlaybookRenderer } from '@/components/business/strategy-doc-playbook';
import { StrategyDocEditor } from '@/components/business/strategy-doc-editor';
import {
  strategyDocRunsQueryOptions,
} from '@/lib/strategy/query-options';
import {
  getStrategyDocMarkdown,
  saveStrategyDocMarkdown,
  setStrategyDocApproval,
  uploadStrategyDoc,
  type StrategyDocRunSummary,
} from '@/lib/strategy/api';

interface StrategyDocPanelProps {
  orgId: number;
  businessId: number;
}

/** Truthy only for a finished, viewable document (generated or uploaded). */
function isViewableDoc(run: StrategyDocRunSummary): boolean {
  return run.status === 'succeeded' && !!run.output_markdown_gcs_uri;
}




function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString();
}

/**
 * Calls tab — the call-driven production pipeline:
 *   1. Upload call transcripts.
 *   2. Generate a strategy doc from the selected transcripts.
 *   3. Build the upload-ready build sheet from a generated doc.
 *
 * The generated documents themselves are read + approved in the Context tab
 * (see {@link StrategyContextPanel}). Only ``source === 'generated'`` runs show
 * here; uploaded docs are not part of the call pipeline.
 */
export function StrategyContextPanel({ orgId, businessId }: StrategyDocPanelProps) {
  const queryClient = useQueryClient();
  const [openRunId, setOpenRunId] = useState<number | null>(null);

  const runsQuery = useQuery(strategyDocRunsQueryOptions(orgId, businessId));
  const runs = runsQuery.data?.runs ?? [];
  // Only finished, viewable documents belong in Context — queued/running/failed
  // generation runs are operational state shown in the Calls tab.
  const docs = runs.filter(isViewableDoc);

  const approveMutation = useMutation({
    mutationFn: ({ runId, approved }: { runId: number; approved: boolean }) =>
      setStrategyDocApproval(orgId, runId, approved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
    },
  });

  return (
    <div className='space-y-8'>
      <DocsSection
        docs={docs}
        isLoading={runsQuery.isLoading}
        onOpen={setOpenRunId}
        onSetApproval={(runId, approved) => approveMutation.mutate({ runId, approved })}
        approvePendingFor={
          approveMutation.isPending ? approveMutation.variables?.runId ?? null : null
        }
      />

      {approveMutation.isError ? (
        <Card className='border-l-4 border-l-[#FCA5A5]'>
          <CardContent className='p-4 text-sm text-[#B91C1C]'>
            {(approveMutation.error as Error).message}
          </CardContent>
        </Card>
      ) : null}

      <UploadDocSection orgId={orgId} businessId={businessId} />

      <RunMarkdownDialog
        orgId={orgId}
        runId={openRunId}
        onClose={() => setOpenRunId(null)}
      />
    </div>
  );
}


function DocsSection({
  docs,
  isLoading,
  onOpen,
  onSetApproval,
  approvePendingFor,
}: {
  docs: StrategyDocRunSummary[];
  isLoading: boolean;
  onOpen: (id: number) => void;
  onSetApproval: (runId: number, approved: boolean) => void;
  approvePendingFor: number | null;
}) {
  return (
    <div className='space-y-3'>
      <div>
        <h3 className='text-base font-semibold text-gray-900'>Strategy Documents</h3>
        <p className='text-sm text-gray-500'>
          The strategy documents for this business. Review each one and approve
          it to mark it as signed off.
        </p>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className='p-4 text-sm text-gray-500'>
            <Spinner size="sm" className="inline mr-2" />
            Loading documents...
          </CardContent>
        </Card>
      ) : docs.length === 0 ? (
        <EmptyState
          icon={<FileText className='h-10 w-10 text-gray-400' />}
          title='No Strategy Documents Yet'
          description='Generate a document from call transcripts in the Calls tab, or upload one below.'
        />
      ) : (
        <Card>
          <CardContent className='divide-y p-0'>
            {docs.map((doc) => (
              <DocRow
                key={doc.id}
                doc={doc}
                onOpen={onOpen}
                onSetApproval={onSetApproval}
                approvePending={approvePendingFor === doc.id}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DocRow({
  doc,
  onOpen,
  onSetApproval,
  approvePending,
}: {
  doc: StrategyDocRunSummary;
  onOpen: (id: number) => void;
  onSetApproval: (runId: number, approved: boolean) => void;
  approvePending: boolean;
}) {
  const isApproved = doc.approved_at !== null;
  const isUploaded = doc.source === 'uploaded';
  return (
    <div className='flex items-center justify-between gap-4 px-4 py-3'>
      <div className='flex min-w-0 items-center gap-3'>
        <FileText className='h-4 w-4 shrink-0 text-gray-500' />
        <div className='min-w-0'>
          <div className='flex items-center gap-2'>
            <p className='truncate text-sm font-medium text-gray-900'>
              {isUploaded ? `Uploaded Doc #${doc.id}` : `Strategy Doc #${doc.id}`}
            </p>
            {isUploaded ? (
              <Badge variant='outline' className='border border-gray-200 text-gray-500'>
                <FileUp className='mr-1 h-3 w-3' />
                Uploaded
              </Badge>
            ) : null}
          </div>
          <p className='truncate text-xs text-gray-500'>
            {formatDate(doc.finished_at ?? doc.created_at)}
            {isApproved ? ` · Approved ${formatDate(doc.approved_at)}` : ''}
          </p>
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-3'>
        <Button variant='outline' size='sm' onClick={() => onOpen(doc.id)}>
          View
        </Button>
        {isApproved ? (
          // Approved: a confirming green state that doubles as the "revoke"
          // affordance. Outline-green keeps the row calm once signed off; the
          // primary green Approve below is reserved for the un-approved action.
          <Button
            variant='outline'
            size='sm'
            className='border-[#6EE7B7] bg-[#D1FAE5] text-[#047857] hover:bg-[#A7F3D0]'
            disabled={approvePending}
            onClick={() => onSetApproval(doc.id, false)}
          >
            {approvePending ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : (
              <CheckCircle2 className='mr-1.5 h-4 w-4' />
            )}
            Approved
          </Button>
        ) : (
          <Button
            size='sm'
            className='bg-green-600 text-white hover:bg-green-700'
            disabled={approvePending}
            onClick={() => onSetApproval(doc.id, true)}
          >
            {approvePending ? (
              <Spinner size="sm" className="mr-1.5" />
            ) : (
              <Check className='mr-1.5 h-4 w-4' />
            )}
            Approve
          </Button>
        )}
      </div>
    </div>
  );
}

const ACCEPTED_DOC_TYPES = '.md,.markdown,.txt';

/**
 * Context-tab upload path for documents prepared outside the call pipeline. An
 * uploaded doc is persisted as a finished, approvable strategy document
 * (``source='uploaded'``) and immediately joins the list above.
 */
function UploadDocSection({ orgId, businessId }: { orgId: number; businessId: number }) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadStrategyDoc(orgId, businessId, file),
    onSuccess: () => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
    },
    onError: (err: Error) => setUploadError(err.message),
  });

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => uploadMutation.mutate(f));
  };

  return (
    <div className='space-y-3'>
      <div className='flex items-end justify-between gap-4'>
        <div>
          <h3 className='text-base font-semibold text-gray-900'>Upload Doc</h3>
          <p className='text-sm text-gray-500'>
            Upload a strategy document prepared outside the call pipeline (.md,
            .markdown, .txt). It joins the documents above and can be reviewed
            and approved like a generated one.
          </p>
        </div>
        <Button
          variant='outline'
          className='h-9 shrink-0 text-gray-900'
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
        >
          {uploadMutation.isPending ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            <Upload className='mr-2 h-4 w-4' />
          )}
          Upload Doc
        </Button>
        <input
          ref={fileInputRef}
          type='file'
          accept={ACCEPTED_DOC_TYPES}
          multiple
          className='hidden'
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!isDragging) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`rounded-lg border-2 border-dashed px-4 py-3 text-center text-sm transition-colors ${
          isDragging
            ? 'border-[#290BD9] bg-[#EDE9FE] text-[#290BD9]'
            : 'border-gray-300 bg-gray-50 text-gray-500'
        }`}
      >
        Drag and drop a document here, or use the Upload button above.
      </div>

      {uploadError ? (
        <Card className='border-l-4 border-l-[#FCA5A5]'>
          <CardContent className='flex items-center gap-2 p-3 text-sm text-[#B91C1C]'>
            <AlertCircle className='h-4 w-4' />
            {uploadError}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Renders a generated build sheet inside an iframe and offers download / open
 * actions. The build sheet is a self-contained HTML document (its own styling +
 * tab-switching JS). It is served to the iframe and the actions via a blob URL
 * rather than `window.open('', '_blank') + document.write` — the latter lands on
 * `about:blank`, is unreliable under popup blockers, and offers no download. A
 * `blob:` URL is a real navigable URL: the page renders with its scripts intact,
 * "Open in New Tab" works, and an anchor with `download` saves the file.
 */
function RunMarkdownDialog({
  orgId,
  runId,
  onClose,
}: {
  orgId: number;
  runId: number | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // `null` draft = view mode; a string = editing (the working copy the editor
  // reports back on every change). Reset whenever the open run changes so one
  // run's edits never bleed into another's view.
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;

  const markdownQuery = useQuery({
    queryKey: ['strategy', orgId, 'doc-run-md', runId],
    queryFn: () => getStrategyDocMarkdown(orgId, runId as number),
    enabled: runId !== null,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // Leaving edit mode on run-switch/close avoids a stale draft showing up
    // against a different document.
    setDraft(null);
  }, [runId]);

  const saveMutation = useMutation({
    mutationFn: (markdown: string) => saveStrategyDocMarkdown(orgId, runId as number, markdown),
    onSuccess: (savedMarkdown) => {
      // Seed the cache from the authoritative saved copy so the read-only
      // view reflects the edit without a refetch, then invalidate the run
      // list (updated_at changed).
      queryClient.setQueryData(['strategy', orgId, 'doc-run-md', runId], savedMarkdown);
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      setDraft(null);
    },
  });

  const loaded = !markdownQuery.isLoading && !markdownQuery.isError;

  return (
    <Dialog open={runId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto p-0'>
        <DialogHeader className='sr-only'>
          <DialogTitle>Strategy Document · Run #{runId ?? ''}</DialogTitle>
        </DialogHeader>
        {markdownQuery.isLoading ? (
          <div className='flex items-center gap-2 p-6 text-sm text-gray-500'>
            <Spinner size="sm" />
            Loading document...
          </div>
        ) : markdownQuery.isError ? (
          <div className='p-6 text-sm text-[#B91C1C]'>
            {(markdownQuery.error as Error).message}
          </div>
        ) : (
          <>
            {/*
              View mode: "Edit" + "Download PDF" (print-to-PDF via the
              PlaybookRenderer stylesheet). Edit mode: "Save" + "Cancel".
              ``data-print-hidden`` keeps this toolbar out of the printed page.
            */}
            <div
              className='sticky top-0 z-20 flex justify-end gap-2 border-b bg-white/95 px-4 py-2 backdrop-blur'
              data-print-hidden
            >
              {isEditing ? (
                <>
                  {saveMutation.isError ? (
                    <span className='mr-auto flex items-center gap-1 self-center text-xs text-[#B91C1C]'>
                      <AlertCircle className='h-3.5 w-3.5' />
                      {(saveMutation.error as Error).message}
                    </span>
                  ) : null}
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-9'
                    disabled={saveMutation.isPending}
                    onClick={() => setDraft(null)}
                  >
                    <X className='mr-2 h-4 w-4' />
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    className='h-9 bg-[#290BD9] hover:bg-[#2309C5]'
                    disabled={saveMutation.isPending || draft.trim().length === 0}
                    onClick={() => saveMutation.mutate(draft)}
                  >
                    {saveMutation.isPending ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <Save className='mr-2 h-4 w-4' />
                    )}
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-9'
                    disabled={!loaded}
                    onClick={() => setDraft(markdownQuery.data ?? '')}
                  >
                    <Pencil className='mr-2 h-4 w-4' />
                    Edit
                  </Button>
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-9'
                    onClick={() => {
                      // Override the document title so the browser's print
                      // header shows the deliverable name, not the global
                      // "GlitchAds" tab title. Restored on the next tick.
                      const prev = document.title;
                      document.title = 'Campaign Launch Playbook';
                      window.print();
                      setTimeout(() => {
                        document.title = prev;
                      }, 0);
                    }}
                  >
                    <Download className='mr-2 h-4 w-4' />
                    Download PDF
                  </Button>
                </>
              )}
            </div>
            {isEditing ? (
              <StrategyDocEditor
                initialMarkdown={markdownQuery.data ?? ''}
                onChange={setDraft}
              />
            ) : (
              <PlaybookRenderer content={markdownQuery.data ?? ''} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}







/**
 * Separate "Corrections" document, opened by its own button on a run row. It
 * lists the doctrine deviations the self-correction loop caught and rewrote the
 * playbook to fix (each cited to a taxonomy node + handbook section), plus any
 * residual deviation the loop could not fully resolve within the pass cap. This
 * is deliberately NOT shown on the playbook itself: the customer doc stays
 * clean and this is the behind-the-scenes record of what was changed.
 */